/* eslint-disable max-lines */
import { FilterValue } from 'antd/es/table/interface';
import { AccountsType } from '@yaotai/common-utils';
import { RoleCrop, UserInfo } from '../hooks';
import { VenueInfo, TimezoneValue } from '.';

export enum IdentityEnum {
  LECTURER = 1,
  COMPERE = 2,
  AUDIENCE = 3,
  ORGANIZER = 4,
  SYSTEM = 11,
}

// 需要判断的前提条件是 cloth_figure_id || hair_figure_id
export enum NielianAndClothChangeEnum {
  None,
  Cloth = 1, // 发生的条件是性别没有发生变化，且身份发生变化了，并且需要进一步判断新身份池子里是否含有现在的衣服
  ClothAndNielian = 2, // 发生的条件是性别发生变化了
}

export type IdentityString = keyof typeof IdentityEnum;

export enum GenderEnum {
  MAN = 1,
  WOMAN,
}

export type GenderString = keyof typeof GenderEnum;

export const Gender = [
  { key: GenderEnum.MAN, text: '男', eng: 'MAN' },
  { key: GenderEnum.WOMAN, text: '女', eng: 'WOMAN' },
];

export const CustomRoleGender = [...Gender, { key: 'ALL', text: '通用', eng: 'ALL' }];

export enum CodeValidStatus {
  None, // 没有门票
  NotEffected, // 未生效
  Effected, // 生效中
  Expired, // 已过期
}

//NOTE: 用于门票的筛选
export enum CodeStatus {
  Waiting,
  Done,
  NotEffected,
  Effected,
  Expired,
}

export const CodeStatusText = {
  [CodeStatus.Waiting]: '未发送',
  [CodeStatus.Done]: '已发送',
  [CodeStatus.NotEffected]: '待生效',
  [CodeStatus.Effected]: '生效中',
  [CodeStatus.Expired]: '失效',
};

export enum PlayerStatusEnum {
  ADOPTED = 1,
  BANED,
  REVIEWING,
}

export const PlayerStatusList = [
  {
    value: PlayerStatusEnum.REVIEWING,
    text: '待审核',
    color: '#f4a923',
  },
  {
    value: PlayerStatusEnum.ADOPTED,
    text: '报名成功',
    color: '#46d0ab',
  },
  {
    value: PlayerStatusEnum.BANED,
    text: '报名失败',
    color: '#f26a3d',
  },
];

export enum PlayerSourceEnum {
  COLLECTIVE = 1,
  PERSONAL,
  GUESTS,
}

export const PlayerSourceList = [
  {
    value: PlayerSourceEnum.COLLECTIVE,
    text: '系统添加',
    filterValue: 'CONCENTRATE_ADD',
  },
  {
    value: PlayerSourceEnum.PERSONAL,
    text: '观众报名',
    filterValue: 'SELF_REGISTER',
  },
  {
    value: PlayerSourceEnum.GUESTS,
    text: '参会游客',
    filterValue: 'GUESTS',
  },
];

export type PlayerCard = {
  id: number;
  avatar?: string;
  user_id: number;
  email: string;
  facial_id?: number;
  facial_flag: boolean;
  name: string;
  organization: string;
  phone: string;
  facial_photo?: {
    photo_url?: string;
    original_photo_url?: string; // 专门用于捏脸的未经压缩的图片
  };
  rank: string;
  figure_id: number;
  hair_figure_id?: number;
  cloth_figure_id?: number;
  character_data: string;
  sex: GenderEnum;
  timezone?: TimezoneValue;
  complete_percent: number;
  identity: number;
  preview_urls?: RoleCrop;
  role_headimg?: string; // 当前选择的role_id对应的头像图
};

export enum CodeSendStatusEnum {
  // 未发送
  NoSend = 1,
  // 短信发送
  MessageSend,
  // 邮件发送
  MailSend,
  // 短信及邮件发送
  MessageAndMailSend,
}

export const CodeSendChannelTextMap = {
  [CodeSendStatusEnum.NoSend]: '-',
  [CodeSendStatusEnum.MessageSend]: '短信',
  [CodeSendStatusEnum.MailSend]: '邮件',
  [CodeSendStatusEnum.MessageAndMailSend]: '短信 邮件',
};

export type TicketGroupInfo = {
  // 0 正式活动 1~n 彩排
  order: number;
  code_valid_time: string[];
  code_valid_status: CodeValidStatus;
  code_send_time: string;
  send_status: CodeSendStatusEnum;
  rehearsal_id?: number;
};

export type PlayerItem = {
  id: number; // 参会人员id
  user_id?: number;
  activity_id: number; //活动表id的外键
  code: string;
  identity: number; // 身份的ID，即后端的枚举成员值
  identity_name: string; // 对应原先前端list里保存的eng，即后端的枚举成员名，与identity一一对应
  send_status: CodeSendStatusEnum;
  meeting_place_id: VenueInfo[];
  source: PlayerSourceEnum;
  status: PlayerStatusEnum;
  timezone?: TimezoneValue;
  code_send_time?: string;
  // 门票的失效时间
  code_expire_time?: string;
  extend_dict?: {
    code_expire_time_parse: string;
    code_valid: boolean;
  };
  name: string;
  email: string;
  organization?: string;
  // 帐号纬度的信息
  user: UserInfo;

  kick_out_time?: string | null;
  // 是否被锁定 0 解锁 1 锁定
  lock_login: number;
  // 扩展字段
  extends_info?: {
    unity_custom: string;
  };
  // 门票组
  ticket_groups: TicketGroupInfo[];
  accounts: AccountsType;
  // 自定义头衔
  custom_title?: {
    choice_flag: boolean;
    chinese_title: string;
    english_title: string;
  };
  // 是否捏脸
  facial_flag?: boolean;
  // 1 会务 2 客户端
  facial_type?: number;
  // 是否在客户端编辑过
  edit_by_client?: boolean;
  // 客户端使用的头像
  avatar?: string;
  code_id?: string;
} & PlayerCard;

// 报名状态
export enum ActivityApplyStatus {
  NotRegistered, // 未报名
  Success,
  Failed,
  UnderReview, // 报名审核中
}

export type RegisterInfoFields = 'name' | 'phone' | 'email' | 'organization' | 'rank';

export type RegisterInfo = Record<RegisterInfoFields, string>;

export enum RehearsalStatus {
  TICKET_TODO = 1,
  WAITING,
  DOING,
  FINISH,
}

export type RehearsalsTime = {
  order: number;
  valid_time: string[];
  reh_activity_status?: RehearsalStatus;
};

// 用户当前报名数量相关的数据
export type ActivityRegisterDetailCountInfo = {
  real_register_count: number; // 自助报名已报名人数
  real_total_count: number; // 已报名总人数
  sale_limit: number; // 公开报名人数上限
  end_time?: string; // 活动结束时间
  tourist_sale_limit?: number; // 游客报名人数上限
};

// 在用户没有登录的时候，活动主页用来获取这个活动的相关信息
export type ActivityRegisterInfoBackend = {
  activity_id: ActivityApplyInfoBackend['activity_info']['activity_id'];
  sale_limit: ActivityApplyInfoBackend['ticket_info']['sale_limit'];
  review_flag: ActivityApplyInfoBackend['ticket_info']['review_flag'];
  email: ActivityApplyInfoBackend['activity_info']['email'];
  already_register_number: ActivityApplyInfoBackend['activity_info']['already_register_number'];
  required_field?: ActivityApplyInfoBackend['activity_info']['required_field'];
  sale_time: ActivityApplyInfoBackend['ticket_info']['sale_time'];
};

// 当前用户在某个活动的权限信息
export type ActivityApplyAuthBackend = {
  identity?: ActivityApplyInfoBackend['identity'];
};

// 后端返回的接口数据，前端转换处理之后的数据定义为 ActivityApplyInfo
export type ActivityApplyInfoBackend = {
  code: string; // 当前用户的门票
  identity?: IdentityEnum; // 当前用户的身份
  code_valid_status: CodeValidStatus; // 当前门票的状态（1：未生效 2：生效中 3：已过期）
  activity_info: {
    already_register_number: number; // 已经公开报名的人数
    email?: string; // 报名开启审核下的审核人邮箱
    activity_id: string;
    required_field?: RegisterInfoFields[]; // 报名必填字段
    activity_code: string; // 活动码
  };
  ticket_info: {
    name: string; // 门票名称
    sale_time: string[]; // 报名时间
    review_flag: boolean; // 是否需要审核
    sale_limit: number; // 门票开放数量
  };
  is_subscribe?: boolean; // 是否已经收藏订阅该活动
  status: ActivityApplyStatus; // 当前活动的报名状态
  valid_time: string[]; // 当前门票有效期
  rehearsals?: RehearsalsTime[]; // 当前用户参与的所有彩排的信息
  rehearsal_id?: number; // 当前用户当前进行中的彩排的 id
  card_info?: PlayerCard;
};

export type ActivityApplyInfo = {
  code: ActivityApplyInfoBackend['code'];
  identity?: ActivityApplyInfoBackend['identity'];
  code_valid_status: ActivityApplyInfoBackend['code_valid_status'];

  activity_info?: ActivityApplyInfoBackend['activity_info'];
  ticket_info?: ActivityApplyInfoBackend['ticket_info'];
  is_subscribe: ActivityApplyInfoBackend['is_subscribe'];
  status?: ActivityApplyInfoBackend['status'];
  valid_time: ActivityApplyInfoBackend['valid_time'];
  rehearsals?: ActivityApplyInfoBackend['rehearsals'];
  rehearsal_id?: ActivityApplyInfoBackend['rehearsal_id'];
  review_flag: ActivityApplyInfoBackend['ticket_info']['review_flag'];

  already_register_number: number; // 当前活动已经注册的用户
  photo_url?: string; // 当前用户当前活动下的头像
  figure_id: number; // 当前用户服装 ID
  err_msg?: string;

  register_limit?: number; // 保持和原来相同的兼容逻辑，同 sale_limit

  registerStatus?: number; // 报名的状态 RegisterStatus
  registerStartTime?: string; // 报名开始的时间
  registerEndTime?: string; // 报名结束的时间
  registerCountdownTarget?: string; // 为经过倒计时组件加工之后的时间倒计时
  applyStatus?: number; // 报名状态 ActivityApplyStatus
  isTicketsSoldOut?: boolean; // 自己判断的是否门票售罄标记位
  required_field?: string[]; // 报名必填字段
};

export type DisplayActivityApplyInfo = Pick<
  ActivityApplyInfo,
  'code' | 'valid_time' | 'photo_url' | 'code_valid_status' | 'identity' | 'rehearsals' | 'rehearsal_id'
>;

export enum PlayerFilterOptions {
  NAME = 'name',
  CODE = 'code',
  EMAIL = 'email',
  PHONE = 'phone',
  ACCOUNT = 'account',
}

export const playerSearchFilterOptions: Array<{ label: string; value: PlayerFilterOptions }> = [
  {
    label: '门票',
    value: PlayerFilterOptions.CODE,
  },
  {
    label: '昵称',
    value: PlayerFilterOptions.NAME,
  },
  {
    label: '登录帐号',
    value: PlayerFilterOptions.ACCOUNT,
  },
];

export type PlayerSearchOption = {
  aid?: string;
  page?: number;
  num?: number;
  sort?: 'create_time';
  direction?: 'asc,desc';
  selectValue?: PlayerFilterOptions;
  inputValue?: string;
  identity?: FilterValue | null;
  source: PlayerSourceEnum;
  send_status?: boolean;
  code_valid_status?: string;
  ticket_type?: string;
  status?: number[];
  [key: string]: any;
  facial_flag?: boolean;
};

export type AtmospherePlayer = {
  code: string;
  identify: IdentityEnum;
  name: string;
};

export enum AtmosphereNameType {
  // 真实姓名
  REAL_NAME = 'real_name',
  // 昵称
  NICK_NAME = 'nick_name',
}

export const atmosphereNameList = [
  {
    value: AtmosphereNameType.REAL_NAME,
    label: '真实姓名',
  },
  {
    value: AtmosphereNameType.NICK_NAME,
    label: '昵称',
  },
];

export type AtmosphereConfig = {
  num: string;
  name_type: AtmosphereNameType;
};

export enum GroupMessageMethodEnum {
  Email = 'email',
  Phone = 'phone',
}

export type GroupMessageFormValues = {
  message?: string;
  methods: GroupMessageMethodEnum[];
};

export enum TicketFilterType {
  Type = 'type',
  Send = 'send',
  Valid = 'valid',
}

export const TicketStatusFilters = [
  {
    value: 'code_send',
    text: '发送状态',
    children: [
      {
        value: `${TicketFilterType.Send}-${CodeStatus.Waiting}`,
        text: CodeStatusText[CodeStatus.Waiting],
      },
      {
        value: `${TicketFilterType.Send}-${CodeStatus.Done}`,
        text: CodeStatusText[CodeStatus.Done],
      },
    ],
  },
  {
    value: 'code_valid',
    text: '门票状态',
    children: [
      {
        value: `${TicketFilterType.Valid}-${CodeValidStatus.NotEffected}`,
        text: CodeStatusText[CodeStatus.NotEffected],
      },
      {
        value: `${TicketFilterType.Valid}-${CodeValidStatus.Effected}`,
        text: CodeStatusText[CodeStatus.Effected],
      },
      {
        value: `${TicketFilterType.Valid}-${CodeValidStatus.Expired}`,
        text: CodeStatusText[CodeStatus.Expired],
      },
    ],
  },
];

export enum ReviewActionsEnum {
  // 审核通过
  ADOPT = 1,
  // 审核拒绝
  BAN,
  // 踢出活动
  KICK,
}

export const TicketTypeStatus = [
  {
    status: CodeValidStatus.NotEffected,
    color: '#3D92E1',
  },
  {
    status: CodeValidStatus.Effected,
    color: '#82BC72',
  },
  {
    status: CodeValidStatus.Expired,
    color: 'rgba(0,0,0,0.45)',
  },
];

export type BulkSendFormValues = {
  is_again: boolean;
  ticket_type: number[];
  player_id_list: number[];
  aid: string;
};

// 批量人员导入详细错误表格
export type ImportResultInfo = {
  row_num?: number;
  num?: number;
  name: string;
  phone: string;
  email: string;
  error_msg: string[];
  sex?: GenderEnum;
  identity?: number;
  organization?: string;
  rank?: string;
  fields?: string[];
};
